// RCPT1-S3 (docs/build-briefs/RCPT1-S3-receipts-and-awaiting-tip.md; PRD P0.9; design/mockups/rcpt1-s3/tip-sheet-entry.html and
// tip-sheet-over-cap.html). The Awaiting tip sheet: the tip a guest wrote on the printed receipt, entered in dollars and cents, or No tip.
// One scrim-backed sheet built at runtime (check:chrome-ratchet refuses a new overlay written into index.html), riding the Confirm
// Payment sheet's own two-column layout (.pay-confirm-sheet) so it reads as the same family.
//
// FLOOR surface, touch only. The tip field is the ONE typed money field here: a decimal inputmode, dollars and cents, named in the
// money-never-typed allowlist (e2e/usability.spec.ts) by its own test id and nowhere else. Nothing is charged by typing: the Charge button
// sends it, and the live line above it ("Tip $5.00, charge $30.13") says what will happen first. Over the limit Charge stays off and
// only two choices are offered: a manager charges the limit, or no tip (a second card charge is not built, RCPT1-S3-F1).
//
// The sheet decides nothing about money. The authorized amount, the limit and the capture are the server's; this draws them and hands the
// chosen tip back. All text via textContent.

import { overCapChoices, readTipEntry, tipSheetView, type TipEntry } from "./receiptTipModel.ts";
import { formatCents } from "./money.ts";

export interface AwaitingTipSheetOptions {
  authorizedCents: number;
  subtotalCents: number;
  taxCents: number;
  /** The most tip the server will capture. */
  tipCapCents: number;
  /** "Visa 4242", or empty. */
  cardLabel: string;
  /** Put back what was typed when the sheet is shown again (after a manager's PIN was cancelled). */
  initialText?: string;
  /** Charge the authorization plus this tip. Resolves undefined when it went through (the sheet closes), or a sentence to show. */
  onCharge: (tipCents: number) => Promise<string | undefined>;
  /** A manager charging the limit. The sheet is closed before this runs, so the approval screens are not covered. */
  onChargeCap: (tipCapCents: number, enteredText: string) => void;
  /** Cancel, the close cross or Escape. */
  onCancel: () => void;
}

export interface AwaitingTipSheetController {
  open: (options: AwaitingTipSheetOptions) => void;
  close: () => void;
  isOpen: () => boolean;
}

const SCRIM_ID = "awaiting-tip-scrim";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  if (testId !== undefined) node.dataset["testid"] = testId;
  return node;
}

function button(
  className: string,
  text: string,
  testId: string,
  onClick: () => void,
): HTMLButtonElement {
  const node = el("button", className, text, testId);
  node.type = "button";
  node.addEventListener("click", onClick);
  return node;
}

export function createAwaitingTipSheet(): AwaitingTipSheetController {
  let scrim: HTMLElement | undefined;
  let busy = false;

  function close(): void {
    scrim?.remove();
    scrim = undefined;
    busy = false;
  }

  function open(options: AwaitingTipSheetOptions): void {
    close();
    let noTipPicked = false;
    let entry: TipEntry = { kind: "empty" };

    const root = el("div", "scrim scrim-top", undefined, SCRIM_ID);
    root.id = SCRIM_ID;
    root.addEventListener("click", (event) => {
      if (event.target === root && !busy) options.onCancel();
    });
    root.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !busy) options.onCancel();
    });

    const sheet = el("div", "sheet pay-confirm-sheet", undefined, "awaiting-tip-sheet");
    sheet.id = "awaiting-tip-sheet";
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-labelledby", "awaiting-tip-title");

    const header = el("div", "sheet-header");
    const titleRow = el("div", "sheet-title-row");
    const title = el("span", "sheet-title", "Awaiting tip");
    title.id = "awaiting-tip-title";
    const closeX = button("btn-icon", "✕", "awaiting-tip-close", () => {
      if (!busy) options.onCancel();
    });
    closeX.setAttribute("aria-label", "Close, do not charge a tip");
    titleRow.append(title, closeX);
    header.append(titleRow);

    const body = el("div", "sheet-body pay-confirm-body", undefined, "awaiting-tip-body");

    const amount = el("div", "pay-confirm-amount");
    amount.append(
      el(
        "span",
        "pay-confirm-amount-label",
        options.cardLabel === "" ? "Authorized" : `Authorized · ${options.cardLabel}`,
        "awaiting-tip-authorized-label",
      ),
      el(
        "span",
        "pay-confirm-amount-value",
        formatCents(options.authorizedCents),
        "awaiting-tip-authorized",
      ),
    );
    const totals = el("div", "ticket-totals pay-confirm-totals");
    const subtotalRow = el("div", "totals-row");
    subtotalRow.append(
      el("span", undefined, "Subtotal"),
      el("span", undefined, formatCents(options.subtotalCents), "awaiting-tip-subtotal"),
    );
    const taxRow = el("div", "totals-row");
    taxRow.append(
      el("span", undefined, "Tax"),
      el("span", undefined, formatCents(options.taxCents), "awaiting-tip-tax"),
    );
    totals.append(subtotalRow, taxRow);

    const live = el("div", "awaiting-tip-live", undefined, "awaiting-tip-live");
    live.setAttribute("role", "status");
    live.setAttribute("aria-live", "polite");

    const over = el("div", "awaiting-tip-over", undefined, "awaiting-tip-over");
    over.hidden = true;
    const choices = overCapChoices(options.authorizedCents, options.tipCapCents);
    const overTitle = el(
      "div",
      "awaiting-tip-over-title",
      choices.title,
      "awaiting-tip-over-title",
    );
    const overOptions = el("div", "awaiting-tip-over-options");
    const overCap = button("btn-secondary", choices.capLabel, "awaiting-tip-over-cap", () => {
      if (busy) return;
      const typed = input.value;
      options.onChargeCap(choices.capTipCents, typed);
    });
    const overNone = button("btn-secondary", choices.noTipLabel, "awaiting-tip-over-none", () => {
      void send(0);
    });
    overOptions.append(overCap, overNone);
    over.append(overTitle, overOptions);

    const errorEl = el("div", "awaiting-tip-error", undefined, "awaiting-tip-error");
    errorEl.setAttribute("role", "alert");
    errorEl.hidden = true;

    const tipSection = el("div", "tip-section");
    const label = el("div", "tip-section-label", "Tip from the receipt");
    label.id = "awaiting-tip-input-label";
    const row = el("div", "tip-custom-row");
    const input = el("input", "field-input tip-custom-input", undefined, "awaiting-tip-input");
    input.id = "awaiting-tip-input";
    input.type = "text";
    input.setAttribute("inputmode", "decimal");
    input.autocomplete = "off";
    input.placeholder = "Tip amount";
    input.setAttribute("aria-labelledby", "awaiting-tip-input-label");
    const noTip = button("btn-secondary btn-compact", "No tip", "awaiting-tip-none", () => {
      if (busy) return;
      noTipPicked = true;
      input.value = "";
      entry = { kind: "empty" };
      render();
    });
    noTip.id = "awaiting-tip-none";
    row.append(input, noTip);
    const capLine = el("div", "awaiting-tip-cap", undefined, "awaiting-tip-cap");
    tipSection.append(label, row, capLine);

    body.append(amount, totals, live, over, errorEl, tipSection);

    const footer = el("div", "sheet-footer pay-confirm-actions");
    const cancel = button("btn-secondary btn-full", "Cancel", "awaiting-tip-cancel", () => {
      if (!busy) options.onCancel();
    });
    const charge = button("btn-pay", "Charge", "awaiting-tip-charge", () => {
      const view = currentView();
      if (busy || !view.chargeEnabled || view.tipCents === null) return;
      void send(view.tipCents);
    });
    charge.id = "awaiting-tip-charge";
    footer.append(cancel, charge);

    sheet.append(header, body, footer);
    root.append(sheet);
    document.body.append(root);
    scrim = root;

    function currentView() {
      return tipSheetView({
        authorizedCents: options.authorizedCents,
        tipCapCents: options.tipCapCents,
        entry,
        noTipPicked,
      });
    }

    function render(): void {
      const view = currentView();
      live.textContent = view.liveLine;
      live.classList.toggle("is-danger", view.liveTone === "danger");
      capLine.textContent = view.capLine;
      capLine.classList.toggle("is-danger", view.overCap);
      input.classList.toggle("is-over", view.overCap);
      input.setAttribute("aria-invalid", view.invalid || view.overCap ? "true" : "false");
      over.hidden = !view.overCap;
      noTip.setAttribute("aria-pressed", noTipPicked ? "true" : "false");
      charge.textContent = view.chargeLabel;
      charge.disabled = busy || !view.chargeEnabled;
      input.disabled = busy;
      noTip.disabled = busy;
      cancel.disabled = busy;
      closeX.disabled = busy;
      overCap.disabled = busy;
      overNone.disabled = busy;
    }

    async function send(tipCents: number): Promise<void> {
      if (busy) return;
      busy = true;
      errorEl.hidden = true;
      errorEl.textContent = "";
      render();
      let problem: string | undefined;
      try {
        problem = await options.onCharge(tipCents);
      } catch {
        problem = "Could not reach the server. Check before charging again.";
      }
      if (scrim !== root) return;
      busy = false;
      if (problem === undefined) {
        close();
        return;
      }
      errorEl.textContent = problem;
      errorEl.hidden = false;
      render();
    }

    input.addEventListener("input", () => {
      noTipPicked = false;
      entry = readTipEntry(input.value);
      errorEl.hidden = true;
      render();
    });

    if (options.initialText !== undefined && options.initialText !== "") {
      input.value = options.initialText;
      entry = readTipEntry(options.initialText);
    }
    render();
    input.focus();
  }

  return {
    open,
    close,
    isOpen: () => scrim !== undefined,
  };
}
